// 「その要素・座標がキャンバス面（canvas-container）の内側か」を判定する共有述語。
//
// ツールバー・ミニマップ・アプリメニュー・コメントピン（fixed の別レイヤ）などの UI は
// CanvasStage の兄弟要素として描画されるため、canvas-container の配下かどうかで
// 「キャンバス面か、その上に重なる UI か」を判別できる。
//
// 空白部では、内側の stage div と CanvasGrid が pointer-events: none でヒットテストを
// 貫通するため、elementFromPoint は canvas-container 自身を返す。
// Node.contains は自身を含むため、この述語で正しく面内と判定できる
// （contains(el) && el !== container のように書くと、空白へのドロップが常に弾かれる）。
//
// なお同義の判定が features/canvas/input/use-global-shortcuts.ts の isWithinCanvasContainer に
// 残っている（data-testid 依存の形）。統合は変更範囲を必要最小限に保つため本対応のスコープ外とし、
// 将来の改善対象とする。

/** 指定ノードがキャンバス面（container 自身を含む）の内側か。 */
export function isNodeOnCanvasSurface(container: HTMLElement | null, node: EventTarget | null): boolean {
  return node instanceof Node && container?.contains(node) === true;
}

/**
 * 指定のクライアント座標にある要素がキャンバス面の内側か。
 *
 * ポインタイベントの `target` は使わない。タッチ / ペンでは pointerdown の対象要素へ
 * 暗黙のポインタキャプチャが張られ、以降の pointermove / pointerup の target が
 * 常に掴んだ要素（接続開始ドット）になるため、UI の上で離しても面内と誤判定してしまう。
 *
 * 受け入れたトレードオフ: elementFromPoint は同期ヒットテストのためスタイル / レイアウトの
 * 強制 flush を伴い、接続ドラッグ中は pointermove ごとに 1 回増える（接続先が取れている
 * 既存経路でも呼ばれる）。呼び出し頻度は draft 更新による再描画と同オーダーであり許容する。
 * 実機で体感の劣化が見られた場合は「ゴースト表示側のみ間引き、確定時（pointerup / armed の
 * pointerdown）は必ず計算する」縮退案を採る。
 */
export function isPointOnCanvasSurface(
  container: HTMLElement | null,
  clientX: number,
  clientY: number
): boolean {
  // jsdom など elementFromPoint を持たない環境では判定不能。面内として扱い、
  // 呼び出し側の他のガード（接続先の有無・抑止条件）に委ねる。
  if (typeof document.elementFromPoint !== "function") return true;
  return isNodeOnCanvasSurface(container, document.elementFromPoint(clientX, clientY));
}
